import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GroupRegisterRequest } from '@app/core/models/auth/group-register-request';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { Option } from '@interfaces/common/option';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { UserService } from '@services/http/user.service';
import { SharedModule } from '@shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Permissions } from 'src/app/core/guard/Permissions';
import { PermissionScreenPanelComponent } from './components/permission-screen-panel/permission-screen-panel.component';
import { PermissionSearchResultsComponent } from './components/permission-search-results/permission-search-results.component';
import { PermissionSummaryComponent } from './components/permission-summary/permission-summary.component';
import { PermissionTreeComponent } from './components/permission-tree/permission-tree.component';
import { roleLabel } from './permission-tree/permission-labels';
import { PermissionScreenNode, PermissionTreeNode } from './permission-tree/permission-tree.models';
import { GroupPermissionsStateService } from './services/group-permissions-state.service';

/**
 * Cadastro e edição de grupo de permissão (card 3902). Tipo (papel) e nome no topo; abaixo, a árvore
 * Módulo › Área › Tela do menu e o painel da tela escolhida. Regras da árvore no README da pasta.
 *
 * O payload continua o mesmo de sempre: `{ name, roleId, permissions }`, com `permissions` = claims marcadas.
 */
@Component({
  selector: 'app-group-register',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    PermissionTreeComponent,
    PermissionScreenPanelComponent,
    PermissionSearchResultsComponent,
    PermissionSummaryComponent,
  ],
  templateUrl: './group-register.component.html',
  styleUrls: ['./group-register.component.scss'],
  providers: [GroupPermissionsStateService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupRegisterComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly userService = inject(UserService);
  private readonly utilsService = inject(UtilsService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);
  private readonly toast = inject(ToastHelperService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly state = inject(GroupPermissionsStateService);
  readonly groupPermissions = Permissions.PermissionGroup;

  readonly form = this.formBuilder.group({
    name: ['', [Validators.required]],
    roleId: ['', [Validators.required]],
  });

  readonly groupId = signal('');
  readonly isEditing = computed(() => !!this.groupId());
  readonly roleOptions = signal<Option[]>([]);
  readonly catalogLoading = signal(false);
  readonly catalogError = signal(false);
  /** O catálogo do papel já chegou (mesmo vazio). */
  readonly catalogLoaded = signal(false);
  readonly saving = signal(false);
  private readonly formValid = signal(false);

  readonly pageTitle = computed(() =>
    this.isEditing() ? 'Editar grupo de permissão' : 'Novo grupo de permissão'
  );

  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };
  readonly breadcrumbs = computed<MenuItem[]>(() => [
    { label: 'Gerenciamento' },
    { label: 'Grupos de permissão', routerLink: '/dashboard/groups/list' },
    { label: this.isEditing() ? 'Editar' : 'Novo' },
  ]);

  readonly canSave = computed(
    () => this.formValid() && this.state.hasSelection() && !this.saving() && !this.catalogLoading()
  );

  // "Salvar" curto: o cabeçalho cabe numa linha em 1280 sem cortar o Nome.
  readonly saveLabel = computed(() => (this.isEditing() ? 'Salvar' : 'Criar grupo'));

  readonly roleHint = computed(() =>
    this.isEditing()
      ? 'Não muda depois de criado: define quais permissões aparecem.'
      : 'Define quais permissões aparecem abaixo.'
  );

  /** Por que o Salvar está desligado, dito na tela (não só no hover). Vazio quando dá para salvar. */
  readonly saveBlockedReason = computed(() => {
    if (this.saving() || this.catalogLoading() || !this.catalogLoaded()) return '';
    if (!this.state.hasSelection()) return 'Ligue ao menos uma permissão para salvar o grupo.';
    if (!this.formValid()) return 'Preencha o nome do grupo para salvar.';
    return '';
  });

  /** Na busca, os módulos sem nenhum resultado (rodapé da árvore). */
  readonly modulesWithoutResults = computed(() => {
    if (!this.state.isSearching()) return [];
    const withResults = new Set(this.state.visibleTree().map(module => module.id));
    return this.state
      .modules()
      .filter(module => !withResults.has(module.id))
      .map(module => module.label);
  });

  ngOnInit(): void {
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formValid.set(this.isFormValidIgnoringDisabled()));

    this.form.controls.roleId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(roleId => this.onRoleChange(roleId ?? ''));

    this.loadRoles();

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params['id']) {
        this.groupId.set(params['id']);
        this.loadGroup(params['id']);
      }
    });
  }

  // ── Árvore e painel ────────────────────────────────────────────────────────

  onNodeSelect(node: PermissionTreeNode): void {
    if (this.state.isSearching() && node.kind === 'screen') {
      this.state.openScreen(node);
      return;
    }
    this.state.selectNode(node);
  }

  onScreenOpen(screen: PermissionScreenNode): void {
    this.state.openScreen(screen);
  }

  onSearch(query: string): void {
    this.state.query.set(query ?? '');
  }

  // ── Ações da página ────────────────────────────────────────────────────────

  cancel(): void {
    if (!this.state.changes().length) {
      this.goToList();
      return;
    }
    this.confirmation
      .confirm(
        'Sair sem salvar?',
        'As permissões que você marcou ou desmarcou nesta tela não serão gravadas.',
        'danger',
        'Continuar editando',
        'Sair sem salvar'
      )
      .subscribe(confirmed => {
        if (confirmed) this.goToList();
      });
  }

  save(): void {
    if (this.form.controls.name.invalid || !this.form.getRawValue().roleId) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.state.hasSelection()) return;

    // Tela de segurança: ganhar gravação (criar, editar, excluir) pede confirmação explícita.
    const writeScreens = this.state.screensGainingWrite();
    if (!writeScreens) {
      this.submit();
      return;
    }
    this.confirmation
      .confirm(
        'Dar permissão de gravação?',
        `Este grupo passa a criar, editar ou excluir dados em ${writeScreens} ${
          writeScreens === 1 ? 'tela' : 'telas'
        }. Confira em "alterações não salvas" o que muda e salve só se for isso mesmo.`,
        'default',
        'Revisar antes',
        'Salvar'
      )
      .subscribe(confirmed => {
        if (confirmed) this.submit();
      });
  }

  private submit(): void {
    const { name, roleId } = this.form.getRawValue();
    const request = {
      name: name ?? '',
      roleId: roleId ?? '',
      permissions: this.state.payload(),
    } as GroupRegisterRequest;

    this.saving.set(true);
    this.utilsService.setIsLoading(true);
    const request$ = this.isEditing()
      ? this.userService.putPermissionGroup(request, this.groupId())
      : this.userService.postPermissionGroup(request);

    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess(
          this.isEditing() ? 'Grupo atualizado.' : 'Grupo criado.',
          this.isEditing()
            ? 'As permissões valem para os usuários do grupo no próximo acesso.'
            : 'Agora você pode associar usuários a ele em Usuários.'
        );
        this.goToList();
      },
      error: error => {
        this.saving.set(false);
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.form, error, false);
        }
        this.toast.showToastError(
          this.isEditing() ? 'Não foi possível salvar o grupo.' : 'Não foi possível criar o grupo.',
          error?.errors?.length
            ? error.errors[0].message
            : 'Confira a conexão e tente de novo em Salvar. Nada foi gravado.'
        );
      },
    });
  }

  reloadCatalog(): void {
    const roleId = this.form.getRawValue().roleId;
    if (roleId) this.loadCatalog(roleId);
  }

  // ── Carregamento ───────────────────────────────────────────────────────────

  private onRoleChange(roleId: string): void {
    // Na edição o papel não muda: o valor é posto pelo carregamento do grupo, sem evento.
    if (this.isEditing()) return;
    this.state.reset();
    this.catalogLoaded.set(false);
    if (roleId) this.loadCatalog(roleId);
  }

  private loadRoles(): void {
    this.userService.getUserRoles().subscribe({
      next: roles => {
        this.roleOptions.set(
          (roles ?? []).map(role => ({ label: roleLabel(role.name), value: role.id }) as Option)
        );
      },
      error: error => {
        this.toast.showToastError(
          'Não foi possível carregar os tipos de usuário.',
          error?.errors?.length
            ? error.errors[0].message
            : 'Recarregue a página para tentar de novo.'
        );
      },
    });
  }

  private loadGroup(id: string): void {
    this.utilsService.setIsLoading(true);
    this.userService.getPermissionGroupById(id).subscribe({
      next: group => {
        this.utilsService.setIsLoading(false);
        if (!group) return;

        this.form.patchValue({ name: group.name, roleId: group.roleId }, { emitEvent: false });
        this.form.controls.roleId.disable({ emitEvent: false });
        this.formValid.set(this.isFormValidIgnoringDisabled());

        this.state.setInitialSelection(group.permissions ?? []);
        this.loadCatalog(group.roleId);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastError(
          'Não foi possível abrir o grupo.',
          error?.errors?.length
            ? error.errors[0].message
            : 'Volte à lista de grupos e abra o grupo de novo.'
        );
      },
    });
  }

  private loadCatalog(roleId: string): void {
    this.catalogLoading.set(true);
    this.catalogError.set(false);
    this.catalogLoaded.set(false);
    this.userService.getAllUsersPermissions(roleId).subscribe({
      next: catalog => {
        this.catalogLoading.set(false);
        this.catalogLoaded.set(true);
        this.state.loadCatalog(catalog ?? []);
      },
      error: error => {
        this.catalogLoading.set(false);
        this.catalogError.set(true);
        this.toast.showToastError(
          'Não foi possível carregar as permissões deste tipo de usuário.',
          error?.errors?.length ? error.errors[0].message : 'Use Tentar de novo abaixo da busca.'
        );
      },
    });
  }

  /** Nome preenchido e papel escolhido (na edição o papel fica desabilitado e sai do `valid` do form). */
  private isFormValidIgnoringDisabled(): boolean {
    const { name, roleId } = this.form.getRawValue();
    return !!name?.trim() && !!roleId;
  }

  private goToList(): void {
    this.router.navigate(['dashboard/groups/list']);
  }
}
